import { useState, useMemo } from 'react';
import { Group } from '../../../types/groups';

export const useGroupsFilters = (data: Group[], searchQuery: string) => {
  const [appliedFilters, setAppliedFilters] = useState<any>({
    type: { values: [] },
    leader: { values: [] },
    department: { values: [] },
    isFull: { values: [] },
    interaction: { values: [] },
    visibility: { values: [] },
    status: { values: ['Active'] } // Default to Active only
  });

  // Build toggle filter option maps from real data
  const typeOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(data.map(g => g.type).filter(Boolean))).map(type => [type!, type!]))
  ), [data]);

  const leaderOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(data.map(g => g.leader).filter(Boolean))).map(leader => [leader!, leader!]))
  ), [data]);

  const departmentOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(data.map(g => g.department).filter(Boolean))).map(dept => [dept!, dept!]))
  ), [data]);

  const visibilityOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['public', 'Public'],
      ['internal', 'Internal'],
    ])
  ), []);

  const capacityOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['true', 'Full'],
      ['false', 'Not Full'],
    ])
  ), []);

  const interactionOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(data.map(g => g.interaction).filter(Boolean))).map(interaction => [interaction!, interaction!]))
  ), [data]);

  const statusOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['Active', 'Active'],
      ['Inactive', 'Inactive'],
    ])
  ), []);

  // Filter definitions for Filters.Bar
  const filterDefinitions: any = useMemo(() => {
    return [
    {
      field: 'type',
      type: 'toggle',
      defaultLabel: 'Group Type',
      options: typeOptionsMap,
      filterState: appliedFilters.type,
    },
    {
      field: 'leader',
      type: 'toggle',
      defaultLabel: 'Leader',
      options: leaderOptionsMap,
      filterState: appliedFilters.leader,
    },
    {
      field: 'department',
      type: 'toggle',
      defaultLabel: 'Department',
      options: departmentOptionsMap,
      filterState: appliedFilters.department,
    },
    {
      field: 'isFull',
      type: 'toggle',
      defaultLabel: 'Capacity',
      options: capacityOptionsMap,
      filterState: appliedFilters.isFull,
    },
    {
      field: 'interaction',
      type: 'toggle',
      defaultLabel: 'Interaction',
      options: interactionOptionsMap,
      filterState: appliedFilters.interaction,
    },
   {
      field: 'visibility',
      type: 'toggle',
      defaultLabel: 'Visibility',
      options: visibilityOptionsMap,
      filterState: appliedFilters.visibility,
    },
    {
      field: 'status',
      type: 'toggle',
      defaultLabel: 'Status',
      options: statusOptionsMap,
      filterState: appliedFilters.status,
    },
  ];
  }, [typeOptionsMap, leaderOptionsMap, departmentOptionsMap, visibilityOptionsMap, capacityOptionsMap, interactionOptionsMap, statusOptionsMap, appliedFilters]);

  // Apply filtering
  const filteredData = useMemo(() => {
    let rows = [...data];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(r => 
        r.name.toLowerCase().includes(query) || 
        r.leader.toLowerCase().includes(query)
      );
    }

    const selectedTypes: string[] = appliedFilters?.type?.values || [];
    if (selectedTypes.length) {
      rows = rows.filter(r => selectedTypes.includes(r.type));
    }

    const selectedLeaders: string[] = appliedFilters?.leader?.values || [];
    if (selectedLeaders.length) {
      rows = rows.filter(r => selectedLeaders.includes(r.leader));
    }

    const selectedDepartments: string[] = appliedFilters?.department?.values || [];
    if (selectedDepartments.length) {
      rows = rows.filter(r => r.department && selectedDepartments.includes(r.department));
    }

    const selectedVisibility: string[] = appliedFilters?.visibility?.values || [];
    if (selectedVisibility.length) {
      rows = rows.filter(r => selectedVisibility.includes(r.visibility));
    }

    const capacitySelections: string[] = appliedFilters?.isFull?.values || [];
    if (capacitySelections.length === 1) {
      const sel = capacitySelections[0];
      if (sel === 'true') {
        rows = rows.filter(r => !!r.isFull);
      } else if (sel === 'false') {
        rows = rows.filter(r => !r.isFull);
      }
    }

    const selectedInteractions: string[] = appliedFilters?.interaction?.values || [];
    if (selectedInteractions.length) {
      rows = rows.filter(r => selectedInteractions.includes(r.interaction));
    }

    const selectedStatuses: string[] = appliedFilters?.status?.values || [];
    if (selectedStatuses.length) {
      rows = rows.filter(r => selectedStatuses.includes(r.status));
    }

    return rows;
  }, [data, appliedFilters, searchQuery]);

  return {
    appliedFilters,
    setAppliedFilters,
    filterDefinitions,
    filteredData,
  };
};
